import deepFreeze from "deep-freeze";
import {
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
} from "fleet/constant/media-library";
import { PropTypeMediaEntry } from "fleet/prop-types";
import { isVideo } from "fleet/view/media-library/utils";
import PropTypes from "prop-types";
import React from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { ContentEditor } from "./content-editor";
import { ContentGroupEditor } from "./content-group-editor";

class SidebarContentEditor extends React.PureComponent {
  static get propTypes() {
    return {
      categories: PropTypes.arrayOf(PropTypes.string),
      editContent: PropTypes.func,
      editContentGroup: PropTypes.func,
      hideFullscreen: PropTypes.func,
      hideSidebar: PropTypes.func,
      isStrategy: PropTypes.bool,
      mediaEntry: PropTypeMediaEntry,
      showFullscreen: PropTypes.func,
      strategyEditContent: PropTypes.func,
    };
  }

  static get defaultProps() {
    return {
      categories: [],
      editContent: () => {},
      editContentGroup: () => {},
      hideFullscreen: () => {},
      hideSidebar: () => {},
      mediaEntry: {},
      showFullscreen: () => {},
      strategyEditContent: () => {},
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      mediaEntry: { ...props.mediaEntry },
    };
    this.baseConfig = this._genBaseConfig(props);
    this.childStates = {};

    this._getConfig = this._getConfig.bind(this);
    this.getChildState = this.getChildState.bind(this);
    this.saveEdits = this.saveEdits.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { mediaEntry } = this.props;
    if (mediaEntry !== prevProps.mediaEntry) {
      this.setState({ mediaEntry });
    }
  }

  getChildState(componentName, childState) {
    this.childStates[componentName] = childState;
  }

  collectState() {
    return Object.keys(this.childStates).reduce((acc, key) => {
      const getChildState = this.childStates[key];
      const childState = getChildState();
      acc[childState.type] = { ...childState };
      return acc;
    }, {});
  }

  /**
   * Generate the base config for the form for each type.
   * NOTE: some fields will need to be further customized by mimeType or
   * where they are viewed in the application (Media Library vs Strategy).
   * Those customizations should happen when you call _getConfig.
   *
   * @typedef {Object} formConfig
   * @property {string[]} readOnlyFields - Array of read only form fields
   * @property {string[]} excludeFields - Array of excluded form fields
   * @property {function} editAction - the function to call on form submit
   *
   * @typedef {Object} baseConfig
   * @property {formConfig} contentType - key is content type, value is form
   *   configuration for the given content type.
   *
   * @param {Object} props - component props.
   * @return {baseConfig} - Base configuration for all types.
   * @private
   */
  _genBaseConfig(props) {
    const { isStrategy } = props;

    // Do not define editActions here as this is only run once in the
    // constructor. Define them in _getConfig.
    const baseConfig = {
      [MEDIA_ENTRY_APPLICATION]: {
        readOnlyFields: ["name"],
        excludeFields: ["url", "duration", "category", "conflictsWith"],
        editAction: null,
      },
      [MEDIA_ENTRY_CONTENT_GROUP]: {
        readOnlyFields: isStrategy ? ["name"] : [],
        excludeFields: [],
        editAction: null,
      },
      [MEDIA_ENTRY_FOLDER]: {
        readOnlyFields: [],
        excludeFields: [],
        editAction: null,
      },
      [MEDIA_ENTRY_USER_CONTENT]: {
        readOnlyFields: isStrategy ? ["name", "category"] : [],
        excludeFields: isStrategy ? ["url"] : ["url", "conflictsWith"],
        editAction: null,
      },
      [MEDIA_ENTRY_WEBPAGE]: {
        readOnlyFields: [],
        excludeFields: ["uploadDate", "category", "conflictsWith"],
        editAction: null,
      },
    };

    // Make sure the base config is never mutated.
    return deepFreeze(baseConfig);
  }

  /**
   * We get the baseConfig for a given type and further customize
   * config based on the content.
   *
   * @param {string} type - the spot/media entry type.
   * @param {string} mimeType - the mime type.
   * @return {formConfig} - form configuration for the given type.
   * @private
   */
  _getConfig(type, mimeType) {
    const { editContent, editContentGroup, isStrategy, strategyEditContent } =
      this.props;

    const config = { ...this.baseConfig[type] };

    if (!config) {
      console.warn("Invalid type, no config exists.", type);
      return {};
    }

    switch (type) {
      case MEDIA_ENTRY_CONTENT_GROUP:
        config.editAction = isStrategy ? strategyEditContent : editContentGroup;
        break;

      case MEDIA_ENTRY_USER_CONTENT:
        config.editAction = isStrategy ? strategyEditContent : editContent;
        if (isVideo(mimeType)) {
          // TODO(jhohman): Revert this when video durations are fixed.
          // config.readOnlyFields = [...config.readOnlyFields, 'duration'];
          config.excludeFields = [...config.excludeFields, "duration"];
        }
        break;

      case MEDIA_ENTRY_WEBPAGE:
        config.editAction = strategyEditContent;
        break;

      default:
        break;
    }

    return config;
  }

  saveEdits() {
    const { isStrategy, hideSidebar } = this.props;

    const mediaEntry = { ...this.state.mediaEntry };
    const { content, type, mimeType } = mediaEntry;

    const allFormStates = this.collectState();
    const meFormState = { ...allFormStates[type] };

    const config = this._getConfig(type, mimeType);

    if (type === MEDIA_ENTRY_CONTENT_GROUP && isStrategy) {
      const contentFormState = allFormStates[MEDIA_ENTRY_USER_CONTENT];
      content.some((c) => {
        if (c.id === contentFormState.id) {
          Object.assign(c, contentFormState);
          return true;
        }
        return false;
      });
    } else {
      Object.assign(mediaEntry, meFormState);
    }

    config.editAction(mediaEntry, "Changes saved!");

    hideSidebar();
  }

  render() {
    const {
      categories,
      hideFullscreen,
      hideSidebar,
      isStrategy,
      showFullscreen,
    } = this.props;
    const { mediaEntry } = this.state;
    const { content, mimeType, type } = mediaEntry;

    const config = this._getConfig(type, mimeType);
    return (
      <div className="sidebar-content-editor">
        {content ? (
          <ContentGroupEditor
            mediaEntry={mediaEntry}
            categories={categories}
            getConfig={this._getConfig}
            getChildState={this.getChildState}
            hideFullscreen={hideFullscreen}
            isStrategy={isStrategy}
            showFullscreen={showFullscreen}
          />
        ) : (
          <ContentEditor
            mediaEntry={mediaEntry}
            getConfig={this._getConfig}
            getChildState={this.getChildState}
            hideFullscreen={hideFullscreen}
            showFullscreen={showFullscreen}
            categories={categories}
          />
        )}
        <Row>
          <Col xs={12}>
            <div className="pull-right button-spacing">
              <Button
                key="sidebar-content-editor-cancel"
                variant="default"
                className="add-content"
                title="Discard changes"
                onClick={hideSidebar}
              >
                Cancel
              </Button>
              <Button
                key="sidebar-content-editor-submit"
                variant="success"
                className="add-content"
                disabled={!config.editAction}
                onClick={this.saveEdits}
                title="Save changes"
              >
                <i className="fa fa-floppy-o" aria-hidden="true" />
                {" Save"}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

export { SidebarContentEditor };
